import React from 'react';
import { format } from 'date-fns';
import { Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Procurement } from '@/types/database.types';
import { calculateTotalAmount } from '@/utils/procurementCalculations';
import QualityInfoBadge from './QualityInfoBadge';
import WarehouseInfoBadge from './WarehouseInfoBadge';

interface FinanceProcurementsTableProps {
  procurements: Procurement[];
  sortConfig: { key: string; direction: string };
  handleSort: (key: string) => void;
}

const FinanceProcurementsTable: React.FC<FinanceProcurementsTableProps> = ({
  procurements,
  sortConfig,
  handleSort,
}) => {
  const getProductsSummary = (procurement: Procurement) => {
    if (procurement.items && procurement.items.length > 0) {
      if (procurement.items.length === 1) {
        return procurement.items[0].product_name;
      }
      return `${procurement.items.length} products`;
    }
    
    // Fallback to single product name if items not loaded
    return procurement.product_name || 'N/A';
  };

  const getSourceName = (procurement: Procurement) => {
    // First priority: use source_name if available (contains the actual name)
    if (procurement.source_name) {
      const productCount = getProductCount(procurement);
      if (productCount > 1) {
        return `${procurement.source_name} (${productCount} products)`;
      }
      return procurement.source_name;
    }
    
    // Fallback: show source type with badge
    const productCount = getProductCount(procurement);
    const sourceType = procurement.source_type || 'Unknown';
    const capitalizedSource = sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
    
    if (productCount > 1) {
      return `${capitalizedSource} (${productCount} products)`;
    }
    return capitalizedSource;
  };

  const getProductCount = (procurement: Procurement) => {
    if (procurement.items && procurement.items.length > 0) {
      return procurement.items.length;
    }
    return procurement.item_count || 1;
  };

  const getTotalQuantity = (procurement: Procurement) => {
    if (procurement.items && procurement.items.length > 0) {
      return procurement.items.reduce((total, item) => total + item.quantity, 0);
    }
    return procurement.total_quantity || 0;
  };

  const getRequestorName = (procurement: Procurement) => {
    if (!procurement.requestor) {
      return 'Unknown User';
    }

    const { first_name, last_name } = procurement.requestor;
    
    if (first_name && last_name) {
      return `${first_name} ${last_name}`;
    }
    
    if (first_name) {
      return first_name;
    }
    
    if (last_name) {
      return last_name;
    }
    
    return 'Unknown User';
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead onClick={() => handleSort('created_at')} className="w-[180px] cursor-pointer">
            Date {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead>ID</TableHead>
          <TableHead>Source</TableHead>
          <TableHead onClick={() => handleSort('requestor')} className="cursor-pointer">
            Requested By {sortConfig.key === 'requestor' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead>Products</TableHead>
          <TableHead className="text-right">Quantity</TableHead>
          <TableHead>Quality</TableHead>
          <TableHead>Warehouse</TableHead>
          <TableHead className="text-right cursor-pointer" onClick={() => handleSort('total_amount')}>
            Total Amount {sortConfig.key === 'total_amount' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
          </TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {procurements.map((procurement) => (
          <TableRow key={procurement.id}>
            <TableCell>{format(new Date(procurement.created_at), 'MMM d, yyyy')}</TableCell>
            <TableCell className="font-mono text-xs">
              {procurement.id.substring(0, 8)}...
            </TableCell>
            <TableCell>
              {getSourceName(procurement)}
            </TableCell>
            <TableCell>
              <div className="flex items-center">
                <Badge variant="outline" className="text-xs">
                  {getRequestorName(procurement)}
                </Badge>
              </div>
            </TableCell>
            <TableCell>{getProductsSummary(procurement)}</TableCell>
            <TableCell className="text-right">
              {getTotalQuantity(procurement).toFixed(2)} kg
            </TableCell>
            <TableCell>
              <QualityInfoBadge procurement={procurement} compact />
            </TableCell>
            <TableCell>
              <WarehouseInfoBadge procurement={procurement} compact />
            </TableCell>
            <TableCell className="text-right font-medium">
              Br {calculateTotalAmount(procurement).toFixed(2)}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  title="View details"
                >
                  <Link to={`/procurements/${procurement.id}`}>
                    <Eye className="h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  asChild
                >
                  <Link to={`/finance/approval/${procurement.id}`}>
                    Review
                  </Link>
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default FinanceProcurementsTable;
